import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { X, Building2, Rocket, XCircle, RotateCcw, Loader2, Check } from "lucide-react";
import { PLANS } from "@/lib/plans";
import { useDemoProfile } from "@/context/DemoProfileContext";
import { DemoUpgradeModal } from "@/components/DemoUpgradeModal";
import { CancelDemoModal } from "@/components/CancelDemoModal";
import { apiPost } from "@/lib/api";
import { getGetMeQueryKey } from "@workspace/api-client-react";

interface Props {
  isDemo: boolean;
  companyId?: string;
  companyName?: string;
  companyPlan?: string;
  userEmail?: string;
}

export function DemoBanner({ isDemo, companyId, companyName, companyPlan, userEmail }: Props) {
  const { profile, setPlanId, planConfigs } = useDemoProfile();
  const [dismissed, setDismissed]       = useState(false);
  const [upgradeOpen, setUpgradeOpen]   = useState(false);
  const [cancelOpen, setCancelOpen]     = useState(false);
  const [resetState, setResetState]     = useState<"idle" | "confirm" | "loading" | "done">("idle");
  const qc = useQueryClient();
  const [, setLocation] = useLocation();

  if (!isDemo || dismissed) return null;

  const isEphemeral  = !!companyId;
  const displayName  = companyName ?? "Demo";
  const displayEmail = userEmail ?? "";
  const effectivePlanId = profile?.planId ?? companyPlan;

  const handleReset = async () => {
    if (resetState === "idle") { setResetState("confirm"); return; }
    if (resetState !== "confirm") return;
    setResetState("loading");
    try {
      const result = await apiPost<{ success: boolean; slug?: string }>("/auth/demo/reset", {});
      await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
      qc.clear();
      setResetState("done");
      if (result.slug) {
        setTimeout(() => setLocation(`/app/${result.slug}`), 400);
      } else {
        setTimeout(() => window.location.reload(), 400);
      }
    } catch {
      setResetState("idle");
    }
  };

  return (
    <>
      <div
        className="w-full flex items-center justify-between px-3 sm:px-6 py-2 gap-3 flex-wrap z-50 shrink-0"
        style={{ background: "linear-gradient(90deg, #b45309 0%, #d97706 50%, #ca8a04 100%)" }}
      >
        {/* Left: company name + mode label */}
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-3.5 h-3.5 text-white/80 shrink-0" />
          <span className="text-sm text-white font-semibold truncate max-w-[180px]">{displayName}</span>
          <span className="text-white/40 hidden sm:inline">·</span>
          <span className="text-xs text-white/70 hidden sm:inline">Demo Mode</span>
        </div>

        {/* Centre: plan switcher */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-white/70 mr-1.5 hidden sm:inline">Switch plan:</span>
          {PLANS.map(p => {
            const dynLabel = planConfigs.find(c => c.planId === p.id)?.label ?? p.short;
            return (
              <button
                key={p.id}
                onClick={() => setPlanId(p.id)}
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-all ${
                  effectivePlanId === p.id
                    ? "text-amber-900"
                    : "text-white/80 hover:text-white border border-white/30 hover:border-white/60"
                }`}
                style={effectivePlanId === p.id ? { background: "#FEF08A", color: "#78350f" } : {}}
              >
                {dynLabel}
              </button>
            );
          })}
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2">
          {/* Start Over (ephemeral demos only) */}
          {isEphemeral && (
            resetState === "confirm" ? (
              <div className="flex items-center gap-1.5 bg-amber-500/20 border border-amber-400/40 rounded-lg px-2 py-1">
                <span className="text-xs text-amber-200 font-semibold hidden sm:inline">Reset all data?</span>
                <button
                  onClick={handleReset}
                  className="text-xs font-bold text-white bg-amber-500 hover:bg-amber-400 px-2 py-0.5 rounded transition-colors"
                >
                  Yes, reset
                </button>
                <button
                  onClick={() => setResetState("idle")}
                  className="text-xs text-amber-300/70 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : resetState === "loading" ? (
              <div className="flex items-center gap-1.5 text-xs text-amber-300 border border-amber-400/30 px-3 py-1.5 rounded-lg">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="hidden sm:inline">Resetting…</span>
              </div>
            ) : resetState === "done" ? (
              <div className="flex items-center gap-1.5 text-xs text-emerald-300 border border-emerald-400/30 px-3 py-1.5 rounded-lg">
                <Check className="w-3 h-3" />
                <span className="hidden sm:inline">Done!</span>
              </div>
            ) : (
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200 border border-amber-400/30 hover:border-amber-400/60 px-3 py-1.5 rounded-lg transition-colors"
                title="Wipe demo data and start fresh"
              >
                <RotateCcw className="w-3 h-3" />
                <span className="hidden sm:inline">Start Over</span>
              </button>
            )
          )}

          {/* Cancel demo */}
          <button
            onClick={() => setCancelOpen(true)}
            className="flex items-center gap-1.5 text-xs font-semibold text-rose-300 hover:text-rose-200 border border-rose-400/30 hover:border-rose-400/60 px-3 py-1.5 rounded-lg transition-colors"
            title="End demo session"
          >
            <XCircle className="w-3 h-3" />
            <span className="hidden sm:inline">Cancel Demo</span>
            <span className="sm:hidden">Cancel</span>
          </button>

          {/* Sign-up CTA */}
          {isEphemeral ? (
            <button
              onClick={() => setUpgradeOpen(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-white/12 hover:bg-white/22 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Rocket className="w-3 h-3" />
              <span className="hidden sm:inline">Sign up to save data</span>
              <span className="sm:hidden">Sign up</span>
            </button>
          ) : (
            <Link
              href="/signup"
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-white/12 hover:bg-white/22 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Rocket className="w-3 h-3" />
              <span className="hidden sm:inline">Sign up to save data</span>
              <span className="sm:hidden">Sign up</span>
            </Link>
          )}

          {/* Dismiss banner */}
          <button
            onClick={() => setDismissed(true)}
            className="text-white/35 hover:text-white/65 transition-colors p-0.5"
            title="Dismiss banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isEphemeral && (
        <DemoUpgradeModal
          open={upgradeOpen}
          email={displayEmail}
          companyName={displayName}
          currentPlanId={effectivePlanId}
          onClose={() => setUpgradeOpen(false)}
        />
      )}

      <CancelDemoModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
      />
    </>
  );
}
